/* Shared media behaviour for the Portfolio showcase and Featured video sections.

   <oorla-rail>         a horizontal scroller with prev/next buttons
   <oorla-client-wall>  a muted film that plays only while on screen, beside
                        a column of client marks matched to its height
   <oorla-film>         a poster that loads and plays its film on press

   Every element is guarded, so the file is safe to include from more than
   one section on a page. */

(() => {
  const oorlaReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('oorla-rail')) {
    class OorlaRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rail-track]');
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        if (!this.track) return;

        this.measure = () => {
          const { scrollLeft, clientWidth, scrollWidth } = this.track;
          if (this.prev) this.prev.disabled = scrollLeft <= 4;
          if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 4;
        };
        this.nudge = (direction) => {
          this.track.scrollBy({
            left: direction * Math.max(260, this.track.clientWidth * 0.8),
            behavior: oorlaReducedMotion() ? 'auto' : 'smooth',
          });
        };
        this.onPrev = () => this.nudge(-1);
        this.onNext = () => this.nudge(1);

        this.track.addEventListener('scroll', this.measure, { passive: true });
        if (this.prev) this.prev.addEventListener('click', this.onPrev);
        if (this.next) this.next.addEventListener('click', this.onNext);
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.track);
        this.measure();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.measure);
        if (this.prev) this.prev.removeEventListener('click', this.onPrev);
        if (this.next) this.next.removeEventListener('click', this.onNext);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }
    }

    customElements.define('oorla-rail', OorlaRail);
  }

  if (!customElements.get('oorla-client-wall')) {
    class OorlaClientWall extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('[data-wall-toggle]');
        this.paused = false;
        this.seen = false;
        if (!this.video) return;

        this.video.muted = true;
        if (this.dataset.videoLabel) this.video.setAttribute('aria-label', this.dataset.videoLabel);
        this.video.addEventListener('contextmenu', this.blockMenu);

        /* Nothing is fetched until the section is reached, and the film only
           runs while it is on screen. */
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.seen = entry.isIntersecting;
            this.sync();
          },
          { threshold: 0.15 }
        );
        this.intersectionObserver.observe(this);

        this.onToggle = () => {
          this.paused = !this.paused;
          this.sync();
        };
        if (this.toggle) this.toggle.addEventListener('click', this.onToggle);

        /* The column matches the film's rendered height, which is only known
           once its metadata lands — so watch the element, not the window. */
        this.resizeObserver = new ResizeObserver(() => {
          this.style.setProperty('--oorla-wall-height', `${this.video.offsetHeight}px`);
        });
        this.resizeObserver.observe(this.video);
      }

      disconnectedCallback() {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
        if (this.video) this.video.removeEventListener('contextmenu', this.blockMenu);
      }

      blockMenu(event) {
        event.preventDefault();
      }

      sync() {
        const play = this.seen && !this.paused && !oorlaReducedMotion();
        if (play) {
          this.video.play().catch(() => {});
        } else if (!this.video.paused) {
          this.video.pause();
        }
        if (this.toggle) {
          const isPaused = this.paused || oorlaReducedMotion();
          this.toggle.classList.toggle('is-paused', isPaused);
          this.toggle.setAttribute('aria-label', isPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
        }
      }
    }

    customElements.define('oorla-client-wall', OorlaClientWall);
  }

  if (!customElements.get('oorla-film')) {
    class OorlaFilm extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-film-play]');
        this.template = this.querySelector('template');
        if (!this.button || !this.template) return;
        this.onPlay = () => this.play();
        this.button.addEventListener('click', this.onPlay);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onPlay);
      }

      /* The film does not exist until it is asked for, which is the only
         preload setting that is actually free. The press is the gesture, so
         playback is requested straight away and focus moves to the player,
         since the control the keyboard was on is about to disappear. */
      play() {
        const stage = this.querySelector('[data-film-stage]');
        if (!stage) return;
        stage.replaceChildren(this.template.content.cloneNode(true));
        const video = stage.querySelector('video');
        if (!video) return;
        video.addEventListener('contextmenu', (event) => event.preventDefault());
        video.focus({ preventScroll: true });
        video.play().catch(() => {
          /* Refused (autoplay policy). The native controls are on screen. */
        });
      }
    }

    customElements.define('oorla-film', OorlaFilm);
  }
})();
